import './card-detail.scss';
import ModalCoreContent from 'components/modal-core-content/modal-core-content';
import { editableCardStatuses } from 'utilities/card-utilities';
import { useSelector } from 'react-redux';
import { switchExpiryDate } from 'utilities/card-utilities';
import EditCardUsageOptionsReadOnly from '../new-card-modal/edit-card-usage-modal/EditCardUsageOptionsReadOnly';
import { Form } from '@ampol/design-system';
import { useSelectCardDetails } from 'selectors/selectors';
import Env from 'Env';

interface CardDetailProps {
  cardNumber: string;
  cardParent?: string;
  onClickEdit: () => void;
}

const Panel = ({ children }) => <div className='p-m bg-background-light'>{children}</div>;

const CardDetail = (props: CardDetailProps) => {
  const cardDetail = useSelectCardDetails();
  const cardDetailLoading = useSelector((state: any) => state.cardDetailView.isLoading);

  const isPreviousOrReplacementCardClicked = useSelector(
    (state: any) => state.cardDetailView.isPreviousOrReplacementCardClicked
  );

  const canReadWrite = useSelector((state: any) => state.userStore.canReadWriteCards);

  const cardDetailError = useSelector((state: any) => state.cardDetailView.isError);

  const isMenuVisible =
    editableCardStatuses.includes(cardDetail?.currentStatus as string) &&
    canReadWrite &&
    !cardDetailLoading &&
    !cardDetailError;

  // Feature flag: When VITE_EV_WAY4 is disabled, block editing for AmpCharge cards
  const isAmpChargeRestricted = !Env.getIsEvWay4Enabled() && cardDetail?.ampChargeEnabled;
  const isEditVisible =
    (isMenuVisible && !isPreviousOrReplacementCardClicked && !isAmpChargeRestricted) ||
    cardDetail?.cardStatus === 'Processing';

  const details = [
    {
      key: 'Customer Name',
      value: cardDetail?.customerName,
    },
    {
      key: 'Emboss Line 2',
      value: cardDetail?.embossedCompanyName,
    },
    {
      key: 'Issue Date',
      value: cardDetail?.issueDate,
    },
    {
      key: 'Expiry Date',
      value: switchExpiryDate(cardDetail?.expiryDate),
    },
    {
      key: 'Renewal Option',
      value: cardDetail?.renewalOption,
    },
    {
      key: 'Cost Centre Code',
      value: cardDetail?.costCentre,
    },
    {
      key: 'Card Current Status',
      value: cardDetail?.cardStatus,
    },
    {
      key: 'Driver Name',
      value: cardDetail?.driverName,
    },
    {
      key: 'Domestic Number',
      value: cardDetail?.domesticNumber,
    },
    {
      key: 'Registration Number',
      value: cardDetail?.registrationNumber,
    },
    {
      key: 'Registration State',
      value: cardDetail?.registrationState,
    },
    {
      key: 'Vehicle Description',
      value: cardDetail?.vehicleDescription,
      uppercase: true,
    },
  ];

  const deliveryDetails = [
    {
      key: 'Contact first name',
      value: cardDetail?.contactFirstName,
    },
    {
      key: 'Contact last name',
      value: cardDetail?.contactLastName,
    },
    {
      key: 'Address line 1',
      value: cardDetail?.addressLine1,
    },
    {
      key: 'Address line 2',
      value: cardDetail?.addressLine2,
    },
    {
      key: 'City',
      value: cardDetail?.city,
    },
    {
      key: 'State',
      value: cardDetail?.state,
    },
    {
      key: 'Postcode',
      value: cardDetail?.postcode,
    },
  ];

  const usageDetails = [
    {
      key: 'Verification Method',
      value: cardDetail?.verifyPosPrompt,
    },
    {
      key: 'Monthly dollar limit',
      value: `$${cardDetail?.monthlyLimit}`,
    },
    {
      key: 'Daily dollar limit',
      value: `$${cardDetail?.dailyLimit}`,
    },
    {
      custom: (
        <Form
          schema={{}}
          defaultValues={{
            odometer: cardDetail?.odometerRequired === 'Yes',
            orderReq: cardDetail?.orderNumberRequired === 'Yes',
          }}
        >
          <Form.Checkbox color='blue' name='odometer' disabled label='Odometer required' className='!mt-[0px]' />
          <Form.Checkbox color='blue' name='orderReq' disabled label='Order no. required' className='!mt-xxs' />
        </Form>
      ),
    },
  ];

  return (
    <div className={'card-detail space-y-m'}>
      <Panel>
        <div className='flex flex-row justify-between align-middle'>
          <h3 className='m-0'>Card {props.cardNumber}</h3>
          {isEditVisible && (
            <button onClick={props.onClickEdit} className='bg-transparent outline-none border-none text-ampol-blue'>
              Edit
            </button>
          )}
        </div>
        <ModalCoreContent data={details} isLoading={cardDetailLoading} />
      </Panel>

      <Panel>
        <h3 className='m-0'>Delivery address</h3>
        <div className='mb-m'>
          <ModalCoreContent data={deliveryDetails} isLoading={cardDetailLoading} />
        </div>
      </Panel>

      <Panel>
        <h3 className='m-0'>Card usage options</h3>
        <div className='mb-m'>
          <ModalCoreContent data={usageDetails} isLoading={cardDetailLoading} />
        </div>
        <EditCardUsageOptionsReadOnly />
      </Panel>
    </div>
  );
};

export default CardDetail;
